import FormLabel from '@c/forms/controls/FormLabel';
import PlacesAutocomplete from '@c/forms/controls/PlacesAutocomplete';
import { useLoadScript } from '@react-google-maps/api';
import Accordion from '@ui/Accordion';
import RadioButton from '@ui/RadioButton';
import { slugify } from '@util/urlHelpers';
import Link from 'next/link';
import { Fragment, useState } from 'react';
import Geocode from 'react-geocode';
import ReactSlider from 'react-slider';

const libraries = ['places'] as 'places'[];

const LocationFilterSection = ({
  popularCities,
  allCities,
  location,
  setLocation,
  radius,
  setRadius,
}: {
  allCities: string[];
  popularCities: string[];
  location?: { lat: number; lng: number; address: string } | null;
  setLocation: ({
    lat,
    lng,
    address,
  }: {
    lat: number;
    lng: number;
    address: string;
  }) => void;
  radius: number;
  setRadius: (radius: number) => void;
}) => {
  const [showMore, setShowMore] = useState(false);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });
  return (
    <Accordion title="Location" startOpen>
      <div className="flex flex-col gap-[3.2rem] p-8">
        <FormLabel value="Radius (mi)" required>
          {/* @ts-ignore */}
          <ReactSlider
            thumbClassName="thumb"
            trackClassName="track"
            min={0}
            max={250}
            value={radius}
            onAfterChange={(e) => setRadius(e)}
            // Warning: A props object containing a "key" prop is being spread into JSX: React keys must be passed directly to JSX without using spread:
            renderThumb={({ key, ...props }, state) => (
              <div key={key} {...props}>
                {state.valueNow}
              </div>
            )}
          />
        </FormLabel>
        <div className="flex flex-col gap-[0.8rem]">
          <span className="font-semibold">Popular Cities</span>
          {popularCities.map((loc) => (
            <Fragment key={loc}>
              <RadioButton
                checked={
                  slugify(loc.replace(',', '')) ===
                  slugify(location?.address ?? '')
                }
                onClick={() => {
                  setLocation({ lat: 0, lng: 0, address: loc });
                }}
                label={loc}
              />
              <Link hidden href={`/dirt-bikes-for-sale-near-${slugify(loc)}`} />
            </Fragment>
          ))}
        </div>
        <button
          className="underline"
          onClick={() => setShowMore((prev) => !prev)}
        >
          {showMore ? 'Hide All Cities' : 'Show All Cities'}
        </button>
        <div
          className={`max-h-[30rem] gap-[0.8rem] overflow-x-hidden overflow-y-scroll whitespace-nowrap p-4 ${
            showMore ? 'block' : 'sr-only'
          }`}
        >
          {allCities
            .sort((a, b) => a.localeCompare(b))
            .map((loc) => {
              if (!popularCities.includes(loc)) {
                return (
                  <Fragment key={loc}>
                    <RadioButton
                      checked={
                        slugify(loc.replace(',', '')) ===
                        slugify(location?.address ?? '')
                      }
                      onClick={() => {
                        setLocation({ lat: 0, lng: 0, address: loc });
                      }}
                      label={loc}
                    />
                    <Link
                      hidden
                      href={`/dirt-bikes-for-sale-near-${slugify(loc)}`}
                    />
                  </Fragment>
                );
              }
            })}
        </div>

        <span>OR</span>
        <FormLabel value="Type a city..." required>
          <div className="flex flex-col">
            {isLoaded && (
              <PlacesAutocomplete
                setSelected={({ address }: { address: string }) => {
                  setLocation({ lat: 0, lng: 0, address });
                }}
              />
            )}

            <button
              className="mt-4 underline"
              onClick={() => {
                new Promise((resolve, reject) => {
                  window.navigator?.geolocation?.getCurrentPosition(
                    (res: {
                      coords: { longitude: number; latitude: number };
                    }) => {
                      resolve({
                        lng: res.coords.longitude,
                        lat: res.coords.latitude,
                      });
                    },
                    (err: any) => {
                      reject(err);
                    }
                  );
                }).then((res: any) => {
                  // Get address from latitude & longitude.
                  Geocode.setApiKey(
                    process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!
                  );
                  Geocode.fromLatLng(res.lat, res.lng).then(
                    (response) => {
                      const address = `${response.results[0]?.address_components[2]?.long_name}, ${response.results[0].address_components[4].short_name}, ${response.results[0].address_components[5].short_name}`;
                      const location = {
                        lat: res.lat,
                        lng: res.lng,
                        address,
                      };
                      setLocation(location);
                    },
                    (error) => {
                      console.error(error);
                    }
                  );
                });
              }}
            >
              Use my current location
            </button>
          </div>
        </FormLabel>
      </div>
    </Accordion>
  );
};

export default LocationFilterSection;
